<template>
    <div class="guide-preview">
        <carousel
            v-if="steps && steps.length"
            :navigation-enabled="true"
            :pagination-enabled="true"
            :per-page="1">
            <slide
                v-for="step in steps"
                :key="step.key">
                <step-preview
                    class="guide-step-preview"
                    :dom="step.dom"
                    :guides="[guide]"
                    :step="step"
                    :is-mobile="isMobile" />
            </slide>
        </carousel>
        <div v-else>
            <pendo-empty-state
                background-color="var(--palette-gray-10)"
                :title="emptyStateTitle"
                :description="emptyStateDescription" />
        </div>
    </div>
</template>

<script>

import StepPreview from '@/guides/p2-guide-details/guide-settings/content-card/step-preview.vue';
import Carousel from '@/common/carousel/carousel.vue';
import Slide from '@/common/carousel/slide.vue';
import { mapActions, mapGetters } from 'vuex';
import { PendoNotification, PendoEmptyState } from '@pendo/components';
import { showGuideSavedNotification } from '@/ask-pendo/utils/ask-pendo-artifact.utils';
import { isMobileApp } from '@/utils/apps';

export default {
    name: 'GuideStepPreviewArtifact',
    components: {
        Carousel,
        PendoEmptyState,
        StepPreview,
        Slide,
    },
    props: {
        artifactId: {
            type: String,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
    },
    data () {
        return {
            isSaved: false,
        };
    },
    computed: {
        ...mapGetters({
            getAppById: 'apps/getAppById',
        }),
        guide () {
            return this.data;
        },
        emptyStateTitle () {
            return this.$t('guideStepPreviewArtifact.Your guide does not have any content');
        },
        emptyStateDescription () {
            return this.$t('guideStepPreviewArtifact.Inform the agent of the guide topic or select edit guide to add content');
        },
        steps () {
            return this.guide?.steps;
        },
        isMobile () {
            if (!this.guide) {
                return false;
            }

            const app = this.getAppById(this.guide.appId);

            return isMobileApp(app?.platform);
        },
        saveButtonLabel () {
            if (this.isSaved) {
                return this.$t('guideStepPreviewArtifact.Saved');
            }

            return this.$t('guideStepPreviewArtifact.Save to Guides');
        },
    },
    mounted () {
        this.updateMenuItems();
    },
    methods: {
        ...mapActions('askPendo', ['setArtifactMenuItems']),
        ...mapActions('guides', ['saveGuide']),
        updateMenuItems () {
            this.setArtifactMenuItems({
                artifactId: this.artifactId,
                items: [
                    {
                        id: 'save-guide',
                        icon: 'save',
                        label: this.saveButtonLabel,
                        disabled: this.isSaved,
                        onClick: () => this.handleSaveGuide(),
                    },
                ],
            });
        },
        async handleSaveGuide () {
            try {
                const savedGuide = await this.saveGuide({ guide: this.guide });
                this.isSaved = true;
                this.updateMenuItems();
                showGuideSavedNotification({
                    guideId: savedGuide.id,
                    router: this.$router,
                    title: this.$t('guideStepPreviewArtifact.notifications.guideSaved'),
                    linkText: this.$t('guideStepPreviewArtifact.notifications.viewGuide'),
                });
            } catch {
                PendoNotification({
                    type: 'error',
                    duration: 3000,
                    title: this.$t('guideStepPreviewArtifact.notifications.guideSaveFailed'),
                    position: 'top-right',
                });
            }
        },

    },
};
</script>

<style scoped lang="less">

:deep(.VueCarousel-pagination) {
    padding-bottom: 16px;
}

.guide-preview {
    position: relative;
    height: 350px;

    .pendo-empty-state{
        background-color: var(--palette-gray-20);
    }
}

.guide-step-preview {
    height: 350px;
}

</style>